import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { getUserAvatarUrl } from '../../utils/api';
import { todayString } from '../../utils/appDateTime';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { useToast } from '../../hooks/use-toast';
import { INDIAN_STATES } from '../../data/indianStates';
import { isMobileContainer } from '../../utils/device';
import { Plus, Edit, UserX, Key } from 'lucide-react';
import MobileInput from '../ui/mobile-input';
import { validateMobile } from '../../utils/phoneValidation';
import { validateDate } from '../../utils/dateValidation';
import CrmEmployeeEditDialog from './CrmEmployeeEditDialog';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin } from '../../utils/crmPermissions';

const dialogClass = 'sm:max-w-lg max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]';

const emptyForm = () => ({
  first_name: '', last_name: '', email: '', phone: '', password: '',
  designation: '', address: '', joining_date: todayString(),
  department_id: '', crm_role_id: '',
  salary_type: 'fixed_plus_incentive', base_salary: '', incentive_per_join: '',
  monthly_target: '', reports_to: '', territories: [{ state: '', district: '', city: '' }],
});

export default function CrmEmployees() {
  const { toast } = useToast();
  const { user } = useAuth();
  const canAssignSuperAdmin = isSuperAdmin(user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [departments, setDepartments] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetId, setResetId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [joiningDateError, setJoiningDateError] = useState('');
  const isMobile = isMobileContainer();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/employees');
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to load', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const loadDepartments = async () => {
    try {
      const res = await api.get('/crm/departments');
      setDepartments(res.data?.data?.items || []);
    } catch {
      setDepartments([]);
    }
  };

  const loadRolesForDepartment = async (departmentId) => {
    if (!departmentId) {
      setRoleOptions([]);
      return [];
    }
    try {
      const res = await api.get('/crm/roles', { params: { department_id: departmentId } });
      const opts = (res.data?.data?.items || [])
        .filter((r) => r.status !== 'inactive')
        .map((r) => ({ value: String(r.id), label: r.role_name }));
      setRoleOptions(opts);
      return opts;
    } catch {
      setRoleOptions([]);
      return [];
    }
  };

  const openCreate = async () => {
    const formData = emptyForm();
    try {
      const res = await api.get('/crm/employees/next-code');
      formData.employee_code = res.data?.data?.employee_code;
    } catch (_) { /* ignore */ }
    await loadDepartments();
    setForm(formData);
    setRoleOptions([]);
    setPhoneError('');
    setJoiningDateError('');
    setCreateOpen(true);
  };

  const onDepartmentChange = async (deptId) => {
    setForm((prev) => ({ ...prev, department_id: deptId, crm_role_id: '' }));
    const opts = await loadRolesForDepartment(deptId);
    if (opts.length) {
      setForm((prev) => ({ ...prev, crm_role_id: opts[0].value }));
    }
  };

  const openEdit = async (row) => {
    setEditingEmployee(row);
    await loadDepartments();
    if (row.department_id) await loadRolesForDepartment(String(row.department_id));
    setEditOpen(true);
  };

  const saveCreate = async () => {
    if (!form.joining_date) {
      toast({ title: 'Joining date required', description: 'Please select a joining date.', variant: 'destructive' });
      return;
    }
    if (!form.department_id || !form.crm_role_id) {
      toast({ title: 'Department and role required', variant: 'destructive' });
      return;
    }
    const dateErr = validateDate(form.joining_date, { required: true });
    if (dateErr) {
      setJoiningDateError(dateErr);
      toast({ title: 'Invalid date', description: dateErr, variant: 'destructive' });
      return;
    }
    const err = validateMobile(form.phone, { required: false });
    if (err) {
      setPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    setPhoneError('');
    setJoiningDateError('');
    try {
      const payload = {
        ...form,
        department_id: Number(form.department_id),
        crm_role_id: Number(form.crm_role_id),
        base_salary: parseFloat(form.base_salary) || 0,
        incentive_per_join: parseFloat(form.incentive_per_join) || 0,
        monthly_target: parseInt(form.monthly_target, 10) || 0,
      };
      delete payload.role;
      await api.post('/crm/employees', payload);
      toast({ title: 'Created', description: 'Employee created successfully' });
      setCreateOpen(false);
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Save failed', variant: 'destructive' });
    }
  };

  const toggleStatus = async (row) => {
    try {
      await api.post(`/crm/employees/${row.id}/deactivate`);
      toast({ title: 'Moved to Bin', description: 'Employee deactivated and moved to Bin.' });
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const resetPassword = async () => {
    try {
      await api.post(`/crm/employees/${resetId}/reset-password`, { password: newPassword });
      toast({ title: 'Password reset' });
      setResetOpen(false);
      setNewPassword('');
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const updateTerritory = (idx, field, value) => {
    const t = [...form.territories];
    t[idx] = { ...t[idx], [field]: value };
    setForm({ ...form, territories: t });
  };

  return (
    <div className="space-y-6">
      <div className={`flex justify-between gap-4 ${isMobile ? 'flex-col items-stretch' : 'items-center'}`}>
        <div>
          <h1 className="text-2xl font-bold">Employees</h1>
          <p className="text-muted-foreground">Manage CRM sales team</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" /> Add Employee
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Team ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p>Loading…</p> : isMobile ? (
            <div className="space-y-3">
              {items.map((row) => (
                <div key={row.id} className="border border-border rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex items-start gap-3 min-w-0">
                      <Avatar className="h-10 w-10 shrink-0">
                        {row.avatar ? (
                          <AvatarImage src={getUserAvatarUrl(row.avatar)} alt={`${row.first_name} ${row.last_name}`} className="object-cover" />
                        ) : null}
                        <AvatarFallback className="bg-blue-500/20 text-blue-600 font-semibold text-sm">
                          {row.first_name?.[0]?.toUpperCase()}{row.last_name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">{row.employee_code}</p>
                        <Link to={`/crm/employees/${row.id}`} className="font-semibold text-blue-600">
                          {row.first_name} {row.last_name}
                        </Link>
                        <p className="text-sm text-muted-foreground">{row.email}</p>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-green-600">
                      active
                    </span>
                  </div>
                  <p className="text-sm">{row.crm_role_name || row.role_name}{row.crm_department_name ? ` · ${row.crm_department_name}` : ''}</p>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(row)}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setResetId(row.id); setResetOpen(true); }}>
                      <Key className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleStatus(row)}>
                      <UserX className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2">Code</th><th>Name</th><th>Email</th><th>Joined</th><th>Role</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="border-b border-border">
                      <td className="py-2">{row.employee_code}</td>
                      <td>
                        <Link to={`/crm/employees/${row.id}`} className="inline-flex items-center gap-2 text-blue-600 hover:underline">
                          <Avatar className="h-8 w-8 shrink-0">
                            {row.avatar ? (
                              <AvatarImage src={getUserAvatarUrl(row.avatar)} alt={`${row.first_name} ${row.last_name}`} className="object-cover" />
                            ) : null}
                            <AvatarFallback className="bg-blue-500/20 text-blue-600 font-semibold text-xs">
                              {row.first_name?.[0]?.toUpperCase()}{row.last_name?.[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span>{row.first_name} {row.last_name}</span>
                        </Link>
                      </td>
                      <td>{row.email}</td>
                      <td>{row.joining_date || '—'}</td>
                      <td>{row.role_name}</td>
                      <td><span className="text-green-600">active</span></td>
                      <td className="py-2 flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEdit(row)}><Edit className="w-3 h-3" /></Button>
                        <Button size="sm" variant="outline" onClick={() => { setResetId(row.id); setResetOpen(true); }}><Key className="w-3 h-3" /></Button>
                        <Button size="sm" variant="outline" onClick={() => toggleStatus(row)}><UserX className="w-3 h-3" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className={dialogClass}>
          <DialogHeader><DialogTitle>Add Employee</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div><Label>Phone</Label><MobileInput value={form.phone} onChange={(v) => { setForm({ ...form, phone: v }); setPhoneError(''); }} error={phoneError} /></div>
            <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
            <div><Label>Joining date <span className="text-red-500">*</span></Label><DateInput required value={form.joining_date} onChange={(v) => { setForm({ ...form, joining_date: v }); setJoiningDateError(''); }} error={joiningDateError} /></div>
            <div><Label>Department <span className="text-red-500">*</span></Label>
              <Select value={form.department_id || undefined} onValueChange={onDepartmentChange}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.department_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Role <span className="text-red-500">*</span></Label>
              <Select value={form.crm_role_id || undefined} onValueChange={(v) => setForm({ ...form, crm_role_id: v })} disabled={!form.department_id}>
                <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{roleOptions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Base salary</Label><Input type="number" value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })} /></div>
              <div><Label>Incentive/join</Label><Input type="number" value={form.incentive_per_join} onChange={(e) => setForm({ ...form, incentive_per_join: e.target.value })} /></div>
            </div>
            <div><Label>Territory (state)</Label>
              <Select value={form.territories[0]?.state || ''} onValueChange={(v) => updateTerritory(0, 'state', v)}>
                <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>District</Label><Input value={form.territories[0]?.district || ''} onChange={(e) => updateTerritory(0, 'district', e.target.value)} /></div>
              <div><Label>City</Label><Input value={form.territories[0]?.city || ''} onChange={(e) => updateTerritory(0, 'city', e.target.value)} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={saveCreate} className="bg-blue-600">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CrmEmployeeEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        employee={editingEmployee}
        onSaved={fetchItems}
      />

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className={dialogClass}>
          <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
          <Input type="password" placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          <DialogFooter>
            <Button onClick={resetPassword} className="bg-blue-600">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
